import FormAddressField from 'components/ui/FormAddressField'
import FormTextAreaField from 'components/ui/FormTextAreaField'
import FormDateField from 'components/ui/FormDateField'
import FormTimeField from 'components/ui/FormTimeField'
import { Form, FormSpy } from 'react-final-form'
import FormCheckBox from 'components/ui/FormCheckBox'
import { ExclamationCircleIcon } from '@heroicons/react/24/outline'
import FormError from 'components/ui/FormError'
import Button from 'components/ui/Button'
import parseISO from 'date-fns/parseISO'
import { useEffect, useMemo, useState } from 'react'
import { SubmissionErrors } from 'final-form'
import FieldSpacer from 'components/ui/FieldSpacer'
import focusOnError from 'components/ui/focusOnError'
import useUser, { User } from 'queries/useUser'
import LoginModal from 'components/LoginModal'
import FormTextField from 'components/ui/FormTextField'
import CompleteProfileModal from 'components/CompleteProfileModal'
import { OptionGroup } from 'components/ui/DeepRadioGroup'
import FormDeepCardRadioGroup from 'components/ui/FormDeepCardRadioGroup'
import { cn } from 'cn'
import FieldDescription from 'components/ui/FieldDescription'
import FormValuesRecorder from 'components/FormValuesRecorder'
import { getSavedFormValues } from 'components/FormValuesRecorder/helpers'
import { ChevronRightIcon } from '@heroicons/react/24/solid'

import MarketRules from './MarketRules'

export type FormValues = {
  eventType: string
  fullAddress: string
  vendingInstructions?: string
  date: Date
  startAt: Date
  endAt: Date
  peopleEstimated: string
  agreedToSalesGuarantee: boolean
  spots: string
  foodPreferences: string | null
}

const validate = (values: Partial<FormValues>) => {
  const errors: Partial<Record<keyof FormValues, string>> = {}
  const requiredFields: (keyof FormValues)[] = [
    'eventType',
    'fullAddress',
    'date',
    'startAt',
    'endAt',
    'foodPreferences',
    'peopleEstimated',
    'spots'
  ]
  requiredFields.forEach((key) => {
    if (!values[key]) {
      errors[key] = 'Required'
    }
  })
  if (values.peopleEstimated && parseInt(values.peopleEstimated, 10) <= 0) {
    errors.peopleEstimated = 'Must be a positive number'
  }
  if (!values.agreedToSalesGuarantee) {
    errors.agreedToSalesGuarantee = 'Required'
  }
  if (values.spots && parseInt(values.spots, 10) <= 0) {
    errors.spots = 'Must be a positive number'
  } else if (values.spots && parseInt(values.spots, 10) > 5) {
    errors.spots = 'Please choose a maximum of 5 food trucks'
  }
  return errors
}

export type Props = {
  onSubmit: (
    values: FormValues,
    user: User
  ) => Promise<SubmissionErrors | undefined>
  categoryOptions: OptionGroup<string>[]
  initialCateringType?: string
}

export default function EventsForm({
  onSubmit,
  categoryOptions,
  initialCateringType
}: Props) {
  const { user } = useUser({ skip: typeof window === 'undefined' })
  const [showLogin, setShowLogin] = useState<boolean>(false)
  const [showCateringSelect, setShowCateringSelection] =
    useState<boolean>(!initialCateringType)
  useEffect(() => {
    if (initialCateringType) {
      setShowCateringSelection(false)
    }
  }, [initialCateringType])
  const [showCompleteProfile, setShowCompleteProfile] = useState<boolean>(false)

  const initialValues = useMemo(() => {
    const savedValues = getSavedFormValues<FormValues>(
      'CATERING_EVENT_FORM_VALUES',
      {
        date: parseISO,
        startAt: parseISO,
        endAt: parseISO
      }
    )

    if (savedValues && Object.keys(savedValues).length > 0) {
      return {
        spots: '1',
        ...savedValues
      }
    }

    return {
      spots: '1',
      eventType: initialCateringType
    }
  }, [initialCateringType])

  return (
    <Form<FormValues>
      decorators={[focusOnError]}
      initialValues={initialValues}
      onSubmit={(values) => {
        if (!user) {
          setShowLogin(true)
          return {}
        }
        if (!user.completeProfile) {
          setShowCompleteProfile(true)
          return {}
        }
        return onSubmit(values, user)
      }}
      validate={validate}
    >
      {({ handleSubmit, submitting }) => (
        <form onSubmit={handleSubmit}>
          <FormValuesRecorder storageKey="CATERING_EVENT_FORM_VALUES" />
          <div className="shadow-md sm:p-10 p-6 rounded-md bg-white">
            {showCateringSelect ? (
              <div>
                <FormDeepCardRadioGroup
                  description="Select the most applicable description of your need"
                  label="What type of a food truck service are you looking for?"
                  name="eventType"
                  optionGroups={categoryOptions}
                  newDesign
                />
              </div>
            ) : (
              <div>
                <label
                  className={cn('block font-medium text-gray-700 mb-2')}
                  htmlFor="eventType"
                >
                  Event occasion
                </label>
                <div
                  id="eventType"
                  onClick={() => setShowCateringSelection(true)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      return setShowCateringSelection(true)
                    }
                    return null
                  }}
                  role="button"
                  tabIndex={0}
                >
                  <span className="italic underline font-medium">
                    {initialCateringType}
                  </span>{' '}
                  <span className="italic">(click to update)</span>
                </div>
              </div>
            )}
            <FieldSpacer variant="normal" />
            <FormTextField
              description="To meet the minimum guarantee you'll want at least 60 people."
              label="How many people do you expect to serve?"
              name="peopleEstimated"
              placeholder="Enter number of guests"
              type="number"
              newDesign
            />
            <FieldSpacer variant="normal" />
            <FormTextField
              description="You'll want at least 60 people per food truck to avoid paying out minimum guarantee payments."
              label="How many food trucks do you need to serve at your event?"
              name="spots"
              type="number"
              newDesign
            />
            <FieldSpacer variant="normal" />
            <div>
              <FormAddressField
                description="Where the event is going to take place"
                label="Address"
                name="fullAddress"
                placeholder="Start typing..."
                newDesign
              />
            </div>
            <FieldSpacer variant="normal" />
            <div>
              <FormTextAreaField
                description="i.e. burgers, mexican, pizza etc."
                label="Cuisine preferences"
                name="foodPreferences"
                newDesign
              />
            </div>
            <FieldSpacer variant="normal" />
            <div>
              <FormTextAreaField
                description="i.e. where to park, onsite contact & mobile, bathroom location, etc."
                label="Additional instructions (optional)"
                name="vendingInstructions"
                newDesign
              />
            </div>
            <FieldSpacer variant="wide" />

            <div className="flex flex-wrap">
              <div className="w-full">
                <FormDateField
                  description="Food trucks book quickly. Earlier requests get better availability."
                  label="Event Date"
                  name="date"
                  newDesign
                />
              </div>
            </div>
            <FieldSpacer variant="wide" />
            <div className="flex flex-wrap">
              <div className="w-1/2">
                <FormTimeField
                  baseDate="date"
                  label="Start Time"
                  name="startAt"
                  newDesign
                />
              </div>
              <div className="w-1/2 pl-2">
                <FormTimeField
                  baseDate="date"
                  label="End Time"
                  name="endAt"
                  newDesign
                />
              </div>
            </div>
            <FieldDescription newDesign>
              Most food truck services run 2–4 hours. Longer events may require
              higher minimums.
            </FieldDescription>
            <FieldSpacer variant="wide" />
            <div>
              <FormSpy subscription={{ values: true }}>
                {({ values }) => {
                  const { fullAddress } = values
                  return <MarketRules fullAddress={fullAddress} />
                }}
              </FormSpy>
              <div className="border border-gray-200 bg-gray-50 rounded-xl p-5">
                <div className="flex items-center gap-2 mb-3">
                  <ExclamationCircleIcon className="h-5 w-5 text-orange-500 shrink-0" />
                  <span className="font-semibold text-gray-900">
                    Minimum sales guarantee
                  </span>
                </div>
                <p className="text-sm text-gray-600 mb-3">
                  All food trucks require a minimum guarantee, typically
                  $600–$1,000. If guest sales don&apos;t reach the minimum, the
                  host covers the difference.
                </p>
                <p className="text-sm text-gray-600 mb-4">
                  <span className="font-semibold text-gray-900">Example:</span>{' '}
                  If the minimum is $1,000 and guests spend $850, the host pays
                  $150. If guests spend $1,000 or more, there is no additional
                  cost.
                </p>
                <FormCheckBox
                  label="I understand and agree to the food truck's minimum sales guarantee."
                  name="agreedToSalesGuarantee"
                  newDesign
                />
              </div>
            </div>
            <FieldSpacer variant="normal" />
            <FormError errorKey="requestInProgress" />
            <FormError />
          </div>
          <div className="flex justify-end py-3 mt-4">
            <LoginModal
              onClose={() => setShowLogin(false)}
              onLogin={() => {
                setShowLogin(false)
                handleSubmit()
              }}
              open={showLogin}
              catering
            />
            {showCompleteProfile && user && (
              <CompleteProfileModal
                onClose={() => setShowCompleteProfile(false)}
                onComplete={() => {
                  setShowCompleteProfile(false)
                  handleSubmit()
                }}
                open={showCompleteProfile}
              />
            )}
            <Button
              className="min-w-[140px] grow sm:grow-0"
              color="primary"
              disabled={submitting}
              type="submit"
              variant="gradient"
            >
              <span>Submit</span>
              <ChevronRightIcon className="h-6 w-6 ml-1" />
            </Button>
          </div>
        </form>
      )}
    </Form>
  )
}
